<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';
import { yup } from '@clearbanc/validation';

import {
  JobTitles,
  isAuthorizedOwner,
  ownerJobTitles,
} from '@clearbanc/data-common-types';
import { getParsedMessage } from '@/data/error-messages';
import { JobTitlesByCorpCountry } from '@/data/owner';
import { RequestStatus } from '@/types/request.types';
import { JobRoles } from '@clearbanc/baus-sdk';
import { useToast } from '@/composables/useToast';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';

import BackButton from '@/components/buttons/BackButton.vue';
import CheckboxGroup from '@/components/forms/groups/CheckboxGroup.vue';
import DropdownGroup from '@/components/forms/groups/DropdownGroup.vue';
import DSButton from 'primevue/button';
import { RouteNames } from '@/data/routes';

// -----
// Setup
// -----

const store = useStore();
const toast = useToast();
const emits = defineEmits([
  'onBack',
  'onSuccess',
  'onError',
  'openInviteModal',
]);

interface BusinessOwner {
  id: string;
  fullName: string;
  isPrimary: boolean;
  jobTitle: JobTitles;
  jobRoles: JobRoles[];
}

const props = defineProps<{
  users: BusinessOwner[];
}>();

// ---------
// Constants
// ---------

const jobTitles = computed(() =>
  JobTitlesByCorpCountry(store.getters.business.corpCountry),
);

const nonBusinessOwnerUsers = computed(() => {
  return props.users.filter((user) => {
    return !isAuthorizedOwner(user.jobTitle, user.jobRoles);
  });
});

const userDropdownOptions = computed(() => {
  return nonBusinessOwnerUsers.value.map((user) => ({
    label: user.fullName,
    value: user.id,
  }));
});

const validationSchema = yup
  .object({
    jobTitle: yup.mixed<JobTitles>().nullable(),

    jobRoles: yup
      .array()
      .of(
        yup
          .string<JobRoles>()
          .oneOf(Object.values(JobRoles), 'Invalid role selected')
          .required(),
      )
      .nullable(),
  })
  .test(
    'job-title-required',
    'Please select a Job Title (must be C-Suite or VP if not an owner)',
    function (values) {
      const { jobTitle, jobRoles } = values;
      const hasOwnerRole = jobRoles && jobRoles.includes(JobRoles.Owner);

      if (!hasOwnerRole) {
        if (!jobTitle) {
          return this.createError({
            path: 'jobTitle',
            message: 'Job title is required when not selecting 25%+ ownership',
          });
        }
        if (!ownerJobTitles.includes(jobTitle)) {
          return this.createError({
            path: 'jobTitle',
            message:
              'Job title must be C-Suite or Vice President when not selecting 25%+ ownership',
          });
        }
      }

      return true;
    },
  )
  .test(
    'owner-role-required',
    'Must select 25%+ ownership when job title is not C-Suite or VP',
    function (values) {
      const { jobTitle, jobRoles } = values;
      const isValidJobTitle = jobTitle && ownerJobTitles.includes(jobTitle);
      const hasOwnerRole = jobRoles && jobRoles.includes(JobRoles.Owner);

      if (!isValidJobTitle && !hasOwnerRole) {
        return this.createError({
          path: 'jobRoles',
          message:
            'Must select 25%+ ownership when job title is not C-Suite or Vice President',
        });
      }

      return true;
    },
  );

// ---------
// Variables
// ---------
const updateUserRequestStatus = computed<RequestStatus>(() =>
  store.getters.requestStatus('UPDATE_USER'),
);

const { handleSubmit, isSubmitting, resetForm, values } = useForm<{
  selectedUserId?: string;
  jobTitle?: JobTitles;
  jobRoles?: JobRoles[];
}>({
  validationSchema,
  validateOnMount: false,
});

const selectedUser = computed(() => {
  return props.users.find((user) => user.id === values.selectedUserId);
});

// -------
// Methods
// -------

const handleError = (error: any) => {
  emits('onError', getParsedMessage(error, true));
};

const onSubmit = handleSubmit(async (values) => {
  await validationSchema.validate(values, {
    stripUnknown: true,
  });

  const { jobTitle, jobRoles } = values;

  // Get existing jobRoles and merge with new ones
  const existingJobRoles = selectedUser.value?.jobRoles || [];
  const newJobRoles = jobRoles || [];
  const mergedJobRoles = [...new Set([...existingJobRoles, ...newJobRoles])];

  const payload = {
    ...(mergedJobRoles.length > 0 && { jobRoles: mergedJobRoles }),
    ...(jobTitle && { jobTitle }),
  };

  await store.dispatchApiAction('UPDATE_USER', {
    id: selectedUser.value?.id,
    ...payload,
  });

  if (updateUserRequestStatus.value.isError) {
    handleError(updateUserRequestStatus.value.error);
  }
  if (updateUserRequestStatus.value.isSuccess) {
    toast.success(`Changes saved.`);
    analytics.track(SEGMENT_EVENTS.if_team_member_set_as_business_owner, {
      source: RouteNames.OWNERS.NEW,
      user_id: selectedUser.value?.id,
      job_title: jobTitle,
      job_roles: mergedJobRoles,
    });
    emits('onSuccess');
  }
  return;
});
</script>

<template>
  <div :class="$style.container">
    <div :class="$style.header">
      <BackButton
        :class="$style['back-button']"
        @click="emits('onBack')"
        data-testid="back-button"
      />
      <h3 :class="$style.title">Add New Owner or Director</h3>
    </div>
    <form
      ref="form"
      class="c-form"
      :class="$style.form"
      :style="{ pointerEvents: isSubmitting ? 'none' : 'all' }"
      @submit.prevent="onSubmit"
    >
      <div class="c-group">
        <div class="c-form__row">
          <div class="mb-5">
            <DropdownGroup
              name="selectedUserId"
              label="Team Member"
              option-label="label"
              option-value="value"
              :options="userDropdownOptions"
              placeholder="Select a Team Member"
              append-to="self"
              data-cy="form-fullName"
              @change="
                (evt: any) => {
                  const user = props.users.find((u) => u.id === evt.value);
                  resetForm({
                    values: {
                      selectedUserId: evt.value,
                      jobTitle: user?.jobTitle || undefined,
                      jobRoles: undefined,
                    },
                  });
                }
              "
            />
          </div>
          <div class="mb-5">
            <DropdownGroup
              v-show="!!selectedUser"
              name="jobTitle"
              :label="$t('common.jobTitle')"
              option-label="label"
              option-value="value"
              :options="jobTitles"
              :placeholder="$t('common.selectOption')"
              append-to="self"
              data-cy="form-jobTitle"
            />
          </div>
          <div>
            <CheckboxGroup
              v-show="!!selectedUser"
              name="jobRoles"
              :options="[
                {
                  value: JobRoles.Owner,
                  label: 'This individual has 25%+ ownership',
                },
              ]"
              data-cy="form-jobRoles"
            />
          </div>
        </div>
        <div>
          <DSButton
            label="Save"
            :class="{
              [$style.actions__button]: true,
              'mt-5': true,
            }"
            :loading="isSubmitting"
            :disabled="!selectedUser"
            type="submit"
          />
        </div>
      </div>
    </form>
    <div :class="$style.footer">
      <a href="#" @click.prevent="emits('openInviteModal')"
        >Can't find team member? Invite them now</a
      >
    </div>
  </div>
</template>

<style scoped module lang="less">
.container {
  padding: 20px;
  margin-top: 40px;
  border: 1px solid #e8e8ea;
  box-shadow: 0 2px 8px 0 #0000000f;
  background: #fff;
}

.header {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 40px;
}

.back-button {
  margin-right: 10px;
}

.title {
  color: var(--neutral-black, #000);
  font-family: @regular-font;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 28px;
  margin: 0;
}

.footer {
  margin-top: 30px;
  text-align: center;
  color: #215cb6;
}

.actions__button {
  width: 100%;
}

@media (min-width: 800px) {
  .container {
    padding: 40px;
  }
}
</style>
